import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useTranslation } from 'react-i18next';
import { Table } from 'design-system-web';
import { AdminPage } from 'components';
import { H3 } from 'components/typography';

import type { InspectionsChannel } from '../types/channel';
import type { InspectionTemplate } from 'inspection-types';
import { useGetInspectionTemplates } from 'react-hooks';
import { SourceType } from 'graphql-query-contracts';
import styles from './InspectionsTemplateLibrary.scss';
import {
  type TemplateLibraryTableRow,
  mapTemplateToTableRow,
  getTemplateLibraryColumns,
  getTemplateLibraryRowActions,
} from './templateLibraryTableConfig';
import { useCustomizeInspectionTemplate } from '../hooks/useCustomizeInspectionTemplate';
import { useInspectionTemplateHideEnabled } from '../hooks/useInspectionTemplateHideEnabled';
import {
  useToggleInspectionTemplateVisibility,
  UNDO_TOAST_DURATION_MS,
  type VisibilityChangedResult,
  type ToggleCompleteResult,
} from '../hooks/useToggleInspectionTemplateVisibility';
import { UndoToast } from '../components/UndoToast';

type Props = {
  channel: InspectionsChannel;
  /** Channel admin route prefix (e.g. match.path from portal), without trailing slash */
  channelBasePath: string;
};

type UndoToastState = {
  isOpen: boolean;
  message: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
};

export function InspectionsTemplateLibrary({
  channel,
  channelBasePath,
}: Props) {
  const { t } = useTranslation();
  const { templates, loading, error, fetchInspectionTemplates, called } =
    useGetInspectionTemplates();
  const { customizeTemplate } = useCustomizeInspectionTemplate({
    channel,
    channelBasePath,
  });
  const hideTemplateEnabled = useInspectionTemplateHideEnabled(channel?._id);

  // Monotonic fetch counter. Each `loadTemplates()` gets the next id; when a
  // fetch's `data` lands we bump `resolvedFetchSeq`. An optimistic override is
  // only allowed to settle against a fetch that was STARTED after the override
  // was applied — a `network-only` refetch fired right after a mutation can
  // return the read-model's pre-change value, and two refetches racing on the
  // shared query slot can resolve out of order. Without this guard a stale
  // response could delete a still-valid override and flip the row back.
  const fetchSeqRef = useRef(0);
  const [resolvedFetchSeq, setResolvedFetchSeq] = useState(0);

  const loadTemplates = useCallback((): Promise<void> => {
    if (!channel?._id) {
      return Promise.resolve();
    }

    fetchSeqRef.current += 1;
    const seq = fetchSeqRef.current;

    const request = fetchInspectionTemplates({
      buildingId: channel._id,
      entityType: 'building',
      sourceType: SourceType.SourceTypeActivate,
      includeHidden: hideTemplateEnabled ? true : undefined,
    });

    // This screen relies on hook state for error UI; the promise resolves
    // either way so callers (the toggle-visibility flow) can await the fetch
    // settling without needing to handle its rejection themselves.
    return request
      ? request
          .then(() => {
            // Record the newest fetch to have settled, so the reconcile effect
            // can tell whether an override has been confirmed by a fresh fetch.
            setResolvedFetchSeq(prev => (seq > prev ? seq : prev));

            return undefined;
          })
          .catch(() => undefined)
      : Promise.resolve();
  }, [channel?._id, fetchInspectionTemplates, hideTemplateEnabled]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const [visibilityOverrides, setVisibilityOverrides] = useState<
    Record<string, { hidden: boolean; appliedAtSeq: number }>
  >({});

  // When a fork occurs during hide/unhide, the refetched list may still
  // contain the parent template instead of the fork (projection lag). This
  // state maps forkedTemplateId → a synthetic template that replaces the
  // parent row so the user sees the change immediately.
  //
  // `expectedHidden` / `appliedAtSeq` let the reconcile effect settle the
  // synthetic row with the SAME guarantees the owned-template overrides use:
  // only drop it once a fetch STARTED after the fork has resolved AND the
  // real fork's `hidden` matches what we set AND the parent is gone. Fork and
  // hide are two independent async events, so a refetch can easily land in an
  // intermediate state (fork present but not yet hidden, or parent not yet
  // removed) — settling on "fork id appeared" alone let the stale parent
  // reappear or the fork show as not-hidden.
  const [templateReplacements, setTemplateReplacements] = useState<
    Record<
      string,
      {
        parentId: string;
        expectedHidden: boolean;
        appliedAtSeq: number;
        syntheticTemplate: InspectionTemplate;
      }
    >
  >({});

  const handleVisibilityChanged = useCallback(
    async ({ templateId, hidden, forkedFromTemplateId }: VisibilityChangedResult) => {
      if (forkedFromTemplateId) {
        // A fork occurred — build a synthetic template from the parent so the
        // row reflects the change immediately, even if the refetch is stale.
        // Capture the fetch seq NOW, before `loadTemplates()` below increments
        // it, so the reconcile effect only settles this against a fetch that
        // STARTED after the fork — never against a pre-fork/stale response.
        const appliedAtSeq = fetchSeqRef.current;

        setTemplateReplacements(prev => {
          const parentTemplate = templates.find(
            tpl => tpl.id === forkedFromTemplateId
          );

          if (!parentTemplate) {
            return prev;
          }

          const syntheticTemplate: InspectionTemplate = {
            ...parentTemplate,
            id: templateId,
            hidden,
            parentTemplateId: forkedFromTemplateId,
            metadata: {
              ...parentTemplate.metadata,
              buildingId: parentTemplate.metadata.buildingId
                ? {
                    ...parentTemplate.metadata.buildingId,
                    entityId: channel._id,
                  }
                : undefined,
            },
          };

          return {
            ...prev,
            [templateId]: {
              parentId: forkedFromTemplateId,
              expectedHidden: hidden,
              appliedAtSeq,
              syntheticTemplate,
            },
          };
        });

        // Also record a visibility override for the fork id. Once the synthetic
        // row is dropped (real fork landed AND hidden matches AND parent gone),
        // this keeps the real fork row showing the intended `hidden` state
        // until the read model fully catches up. Without it the fork would flip
        // back to the server's stale `hidden` the moment the synthetic is gone.
        // It also lets Undo update the still-synthetic row (see effectiveTemplates).
        setVisibilityOverrides(prev => ({
          ...prev,
          [templateId]: { hidden, appliedAtSeq },
        }));
      } else {
        // Owned template — use the lightweight visibility override as before.
        // Record the fetch seq in effect when the override is applied so the
        // reconcile effect only settles it against a fetch started afterwards.
        setVisibilityOverrides(prev => ({
          ...prev,
          [templateId]: { hidden, appliedAtSeq: fetchSeqRef.current },
        }));
      }

      await loadTemplates();
    },
    [channel._id, loadTemplates, templates]
  );

  const [undoToast, setUndoToast] = useState<UndoToastState>({
    isOpen: false,
    message: '',
  });

  const closeUndoToast = useCallback(
    () => setUndoToast(prev => ({ ...prev, isOpen: false })),
    []
  );

  const showUndoToast = useCallback(
    ({ templateName, hidden, undo }: ToggleCompleteResult) => {
      setUndoToast({
        isOpen: true,
        message: t(
          hidden
            ? 'web.admin.inspections.templateLibrary.visibility.hideSuccessToast'
            : 'web.admin.inspections.templateLibrary.visibility.unhideSuccessToast',
          { templateName }
        ),
        actionLabel: t('shared.common.undo'),
        onAction: () => {
          // Hand `undo` the current re-sync callback rather than one captured
          // earlier, so it reconciles against the latest loaded templates.
          undo(handleVisibilityChanged)
            .then(() => {
              // Post-undo confirmation toast: NO action (prevents an
              // undo-the-undo loop). `hidden` is the state BEFORE undo, so the
              // confirmation uses the reverse copy.
              setUndoToast({
                isOpen: true,
                message: t(
                  hidden
                    ? 'web.admin.inspections.templateLibrary.visibility.unhideSuccessToast'
                    : 'web.admin.inspections.templateLibrary.visibility.hideSuccessToast',
                  { templateName }
                ),
              });
            })
            .catch(() => {
              // undo() already showed the error toast; just close ours.
              closeUndoToast();
            });
        },
      });
    },
    [t, closeUndoToast, handleVisibilityChanged]
  );

  const rawTemplatesById = useMemo(
    () => new Map(templates.map(tpl => [tpl.id, tpl])),
    [templates]
  );

  useEffect(() => {
    setVisibilityOverrides(prev => {
      const settledIds = Object.keys(prev).filter(id => {
        const template = rawTemplatesById.get(id);
        const override = prev[id];

        // Only settle once a fetch that STARTED after the override was applied
        // has resolved. This ignores a stale read-model response (or an
        // out-of-order refetch) that predates the override and would otherwise
        // clear a still-valid optimistic value against pre-change server data.
        return (
          template !== undefined &&
          resolvedFetchSeq > override.appliedAtSeq &&
          template.hidden === override.hidden
        );
      });

      if (settledIds.length === 0) {
        return prev;
      }

      const next = { ...prev };

      settledIds.forEach(id => delete next[id]);

      return next;
    });

    // Clean up template replacements only once the real fork has FULLY landed,
    // mirroring the owned-template settle guard above. All four must hold:
    //   1. the fork id appears in the fetched list,
    //   2. a fetch that STARTED after the fork was applied has resolved
    //      (seq guard — defeats stale/out-of-order network-only responses),
    //   3. the fork's `hidden` matches what we expect (the live override, so
    //      an Undo is respected; falling back to the value set at fork time),
    //   4. the parent is gone from the read model.
    // Until all hold, the refetch may be an intermediate projection; dropping
    // the synthetic row early let the stale parent reappear or showed the fork
    // as not-hidden, contradicting the hide/unhide response.
    setTemplateReplacements(prev => {
      const settledForkIds = Object.keys(prev).filter(forkedId => {
        const replacement = prev[forkedId];
        const realFork = rawTemplatesById.get(forkedId);
        const parentStillPresent = rawTemplatesById.has(replacement.parentId);
        const expectedHidden =
          visibilityOverrides[forkedId]?.hidden ?? replacement.expectedHidden;

        return (
          realFork !== undefined &&
          resolvedFetchSeq > replacement.appliedAtSeq &&
          realFork.hidden === expectedHidden &&
          !parentStillPresent
        );
      });

      if (settledForkIds.length === 0) {
        return prev;
      }

      const next = { ...prev };

      settledForkIds.forEach(id => delete next[id]);

      return next;
    });
  }, [rawTemplatesById, resolvedFetchSeq, visibilityOverrides]);

  // Set of parent template ids that have been replaced by a fork. Used to
  // filter them out of the rendered list so the stale parent row disappears.
  const replacedParentIds = useMemo(
    () => new Set(Object.values(templateReplacements).map(r => r.parentId)),
    [templateReplacements]
  );

  // Fork ids that currently have an active synthetic replacement. While a
  // replacement is live we suppress the real fork from the fetched list (if it
  // has already landed) so it doesn't render as a duplicate next to the
  // synthetic row. Once the settle effect drops the replacement, the real fork
  // renders from `base` with its visibility override applied.
  const replacedForkIds = useMemo(
    () => new Set(Object.keys(templateReplacements)),
    [templateReplacements]
  );

  const effectiveTemplates = useMemo(
    () => {
      // Start with the fetched templates, filtering out any parents that were
      // replaced by a fork (and the real forks still shadowed by a synthetic
      // row), then apply visibility overrides.
      const base = templates
        .filter(
          template =>
            !replacedParentIds.has(template.id) &&
            !replacedForkIds.has(template.id)
        )
        .map(template => {
          const override = visibilityOverrides[template.id];

          return override === undefined
            ? template
            : { ...template, hidden: override.hidden };
        });

      // Append synthetic fork templates, applying any visibility override for
      // the fork id so an Undo (which updates the override) is reflected on the
      // synthetic row while the projection is still stale.
      const syntheticTemplates = Object.values(templateReplacements).map(r => {
        const override = visibilityOverrides[r.syntheticTemplate.id];

        return override === undefined
          ? r.syntheticTemplate
          : { ...r.syntheticTemplate, hidden: override.hidden };
      });

      return [...base, ...syntheticTemplates];
    },
    [
      templates,
      visibilityOverrides,
      replacedParentIds,
      replacedForkIds,
      templateReplacements,
    ]
  );

  const templatesById = useMemo(
    () => new Map(effectiveTemplates.map(tpl => [tpl.id, tpl])),
    [effectiveTemplates]
  );

  const { toggleTemplateVisibility } = useToggleInspectionTemplateVisibility({
    channel,
    onVisibilityChanged: handleVisibilityChanged,
  });

  const rows = useMemo(
    () => effectiveTemplates.map(mapTemplateToTableRow),
    [effectiveTemplates]
  );

  const filterTemplatesByName = useCallback(
    (row: TemplateLibraryTableRow, keyword: string) => {
      const q = keyword.trim().toLowerCase();

      if (!q) {
        return true;
      }

      return row.name.toLowerCase().includes(q);
    },
    []
  );

  const columns = useMemo(
    () => getTemplateLibraryColumns(t, { includeVisibilityColumn: hideTemplateEnabled }),
    [t, hideTemplateEnabled]
  );

  // Isolated per column set — see the comment on the Table's `key`/`tableKey`
  // props below for why the visibility column needs its own persisted-order
  // cache bucket rather than sharing one with the base (no-visibility) table.
  const tableKey = `inspections-template-library-v2${
    hideTemplateEnabled ? '-visibility' : ''
  }`;

  const handleCustomize = useCallback(
    (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      customizeTemplate(template);
    },
    [templatesById, customizeTemplate]
  );

  const handleToggleVisibility = useCallback(
    async (row: TemplateLibraryTableRow) => {
      const template = templatesById.get(row.id);

      if (!template) {
        return;
      }

      const completion = await toggleTemplateVisibility(template);

      // `undefined` means nothing completed (cancelled, de-duped, or failed) —
      // only surface the undo toast when a change actually landed.
      if (completion) {
        showUndoToast(completion);
      }
    },
    [templatesById, toggleTemplateVisibility, showUndoToast]
  );

  const isChildChannel = Boolean(channel.parent?._id);

  const rowActions = useMemo(
    () =>
      isChildChannel
        ? getTemplateLibraryRowActions(t, {
            onCustomize: handleCustomize,
            onToggleVisibility: handleToggleVisibility,
            includeVisibilityActions: hideTemplateEnabled,
          })
        : undefined,
    [isChildChannel, t, handleCustomize, handleToggleVisibility, hideTemplateEnabled]
  );

  if (error) {
    return (
      <AdminPage className={styles.adminPage}>
        <div className={styles.templateLibrary}>
          <div className={styles.header}>
            <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
          </div>
          <p>{t('web.admin.inspections.templateLibrary.error')}</p>
        </div>
      </AdminPage>
    );
  }

  return (
    <AdminPage className={styles.adminPage}>
      <div className={styles.templateLibrary}>
        <div className={styles.header}>
          <H3>{t('web.admin.inspections.templateLibrary.title')}</H3>
        </div>
        <Table<TemplateLibraryTableRow>
          columns={columns}
          data={rows}
          isLoading={loading || !called}
          pagination="client"
          defaultPageSize={25}
          rowActions={rowActions}
          rowActionsLabel={t(
            'web.admin.inspections.templateLibrary.actions.label'
          )}
          emptyMessage={t('web.admin.inspections.templateLibrary.emptyState')}
          hasKeywordFilter
          keywordFilterLabel={t(
            'web.admin.inspections.templateLibrary.searchLabel'
          )}
          customKeywordFilterFn={filterTemplatesByName}
          getRowId={row => row.id}
          showColumnVisibility
          // `getMergedColumnOrder` (design-system-web) drops a column key from the
          // persisted order once it disappears from `columns`, then re-appends it
          // at the END the next time it reappears — so if the visibility column
          // shared a cache key with the base table, toggling the hide-template
          // flag off then on would push it to the end every time. Keying the two
          // column sets separately keeps each one's persisted order isolated, so
          // the flag can flip without corrupting the other's column order.
          //
          // `key` forces a remount (not just a prop change) when the flag flips,
          // since usePersistedSetting/useLocalStorage only reads localStorage in
          // its useState initializer — that won't re-run just because the
          // `tableKey` prop changed on an already-mounted Table.
          key={tableKey}
          tableKey={tableKey}
        />
      </div>
      <UndoToast
        isOpen={undoToast.isOpen}
        message={undoToast.message}
        actionLabel={undoToast.actionLabel}
        onAction={undoToast.onAction}
        onDismiss={closeUndoToast}
        duration={UNDO_TOAST_DURATION_MS}
      />
    </AdminPage>
  );
}
